import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  ExclamationCircleOutlined,
  EyeOutlined,
  HolderOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Card,
  Col,
  Dropdown,
  GetProp,
  MenuProps,
  Modal,
  Row,
  Table,
  TablePaginationConfig,
  TableProps,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import Search from 'antd/es/input/Search';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import { Country } from 'models/Country';

import { CountryService } from 'services/CountryService';
import { VerticalService } from 'services/VerticalService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import { VerticalService as VerticalServiceModel } from '../../../models/VerticalService';
import DetailServiceModal from './components/DetailServiceModal';
import NewServiceModal from './components/NewServiceModal';

import './Services.scss';

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

type MainType = VerticalServiceModel;

const PAGE_SIZE = 10;

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const CustomRow: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

function Services() {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextModalHolder] = Modal.useModal();
  const [tableLoading, setTableLoading] = useState(true);
  const [dataSource, setDataSource] = useState<MainType[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<MainType[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [title] = useState<string>('');
  const [countries, setCountries] = useState<Country[]>([]);
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );
  const [selectedRow, setSelectedRow] = useState<MainType>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);

  /* Filters */
  const [term, setTerm] = useState('');

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData(tableParams.pagination?.current);
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
  ]);

  useEffect(() => {
    fetchData(1);
  }, [term]);

  const init = async () => {
    setTableLoading(true);

    const [countryResponse] = await Promise.all([
      CountryService.getAll(),
      fetchDataFromServer(false),
    ]);

    if (countryResponse.success) {
      setCountries(countryResponse.data as Country[]);
    } else {
      const error = countryResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.services.scene.errors.fetch_countries'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const createResourceModal = () => {
    setIsNewResourceModalOpen(true);
  };

  const showDetailResource = async (row: MainType) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  };

  const handleCloseNewResourceModal = () => {
    setIsNewResourceModalOpen(false);
  };

  const handleCloseShowDetailResourceModal = () => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  };

  const getDropdownMenu = (row: MainType): MenuProps['items'] => {
    const menus: MenuProps['items'] = [];
    let itemNro = 1;

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <EyeOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.services.scene.dropdown.view_detail')}
        </span>
      ),
      onClick: () => {
        showDetailResource(row);
      },
    });

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <LockOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.services.scene.dropdown.delete')}
        </span>
      ),
      onClick: async () => {
        await deleteResource(row);
      },
      danger: true,
    });

    return menus;
  };

  const deleteResource = async (row: MainType) => {
    modal.confirm({
      title: t('scenes.settings.services.scene.delete_confirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <span>
          {t('scenes.settings.services.scene.delete_confirm.content', {
            id: row.id,
          })}
        </span>
      ),
      okText: t('scenes.settings.services.scene.delete_confirm.ok'),
      cancelText: t('scenes.settings.services.scene.delete_confirm.cancel'),
      onOk: async () => {
        setTableLoading(true);
        const response = await VerticalService.delete(row.id);

        if (response.success) {
          await fetchDataFromServer();
          messageApi.success(
            <span>
              {t('scenes.settings.services.scene.messages.delete_success', {
                id: row.id,
              })}
            </span>
          );
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t('scenes.settings.services.scene.errors.delete_failed'),
            3.5
          );
        }

        setTableLoading(false);
      },
    });
  };

  const handleTableChange: TableProps<MainType>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const sorterLocal = sorter as any;

    setTableParams({
      pagination,
      filters,
      sortOrder:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.order
          : undefined,
      sortField:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.columnKey
          : undefined,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setFilteredDataSource([]);
    }
  };

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) {
      setTableLoading(true);
    }
    const response = await VerticalService.getAll();

    if (response.success) {
      setDataSource(response.data as MainType[]);
      fetchData(1, response.data as MainType[]);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.services.scene.errors.fetch_services'),
        3.5
      );
    }

    if (withLoading) {
      setTableLoading(false);
    }
  };

  const fetchData = (currentPage = 1, data?: MainType[]) => {
    const termFilter = FunctionsHelper.normalizeText(term.trim());

    let filteredData = data !== undefined ? data : [...dataSource];

    if (termFilter.length > 0) {
      filteredData = filteredData.filter((record) => {
        return (
          FunctionsHelper.normalizeText(record.name).includes(termFilter) ||
          FunctionsHelper.normalizeText(record.title).includes(termFilter)
        );
      });
    }

    const total = filteredData.length;

    setFilteredDataSource(filteredData);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: currentPage,
        total: total,
      },
    });
  };

  const getCountryName = (countryCode: string) => {
    const country = countries.find((record) => {
      return record.code === countryCode;
    });

    return country ? country.name : countryCode;
  };

  const columns: TableColumnsType<MainType> = [
    {
      key: 'sort',
      align: 'center',
      width: 80,
      render: () => <DragHandle />,
    },
    {
      title: t('scenes.settings.services.scene.table.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
    },
    {
      title: t('scenes.settings.services.scene.table.columns.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 180,
    },
    {
      title: t('scenes.settings.services.scene.table.columns.title'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      key: 'country',
      title: t('scenes.settings.services.scene.table.columns.country'),
      align: 'center',
      width: 140,
      render: (row: MainType) => <span>{getCountryName(row.countryCode)}</span>,
    },
    {
      key: 'createdAtFormatted',
      title: t('scenes.settings.services.scene.table.columns.created_at'),
      dataIndex: 'createdAt',
      width: 160,
      align: 'center',
      render: (createdAt: Date) => {
        return <span>{dayjs(createdAt).format(dateFormat)}</span>;
      },
    },
    {
      key: 'actions',
      width: 120,
      align: 'center',
      render: (row: MainType) => {
        return (
          <Dropdown
            menu={{ items: getDropdownMenu(row) }}
            placement="bottomLeft"
            trigger={['click']}
          >
            <Button size="small">
              <MoreOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const originalDataSource = [...prevState];
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );
        const newDataSource = arrayMove(prevState, activeIndex, overIndex);
        fetchData(tableParams.pagination?.current, newDataSource);
        handleDragEndUpdate(newDataSource, originalDataSource);
        return newDataSource;
      });
    }
  };

  const handleDragEndUpdate = async (
    newDataSource: MainType[],
    originalDataSource: MainType[]
  ) => {
    setTableLoading(true);

    const verticalServiceIds = newDataSource.map(
      (verticalService) => verticalService.id
    );
    const response = await VerticalService.uploadPositions(verticalServiceIds);

    setTableLoading(false);

    if (!response.success) {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.services.scene.errors.update_positions_failed'),
        3.5
      );
      fetchData(tableParams.pagination?.current, originalDataSource);
      setDataSource(originalDataSource);
    }
  };

  const createNewResource = async (
    countryCode: string,
    name: string,
    title: string,
    description: string,
    logoUrl: string,
    mainContactName: string,
    mainContactDialCode: string,
    mainContactPhone: string,
    mainContactEmail: string,
    primaryColor: string
  ) => {
    setTableLoading(true);

    const response = await VerticalService.create({
      countryCode,
      name,
      title,
      description,
      logoUrl,
      mainContactName,
      mainContactDialCode,
      mainContactPhone,
      mainContactEmail,
      additionalData: {
        primaryColor,
      },
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.services.scene.messages.create_success', {
          id: (response.data as { createdId: number }).createdId,
        })
      );
      handleCloseNewResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.services.scene.errors.create_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const editNewResource = async (
    id: number,
    name: string,
    title: string,
    description: string,
    logoUrl: string,
    mainContactName: string,
    mainContactDialCode: string,
    mainContactPhone: string,
    mainContactEmail: string,
    primaryColor: string
  ) => {
    setTableLoading(true);

    const response = await VerticalService.update(id, {
      name,
      title,
      description,
      logoUrl,
      mainContactName,
      mainContactDialCode,
      mainContactPhone,
      mainContactEmail,
      additionalData: {
        primaryColor,
      },
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.services.scene.messages.update_success', { id })
      );
      handleCloseShowDetailResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.services.scene.errors.update_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  return (
    <Layout title={title}>
      {contextHolder}
      {contextModalHolder}

      <Card>
        <div className="filter-container">
          <Row gutter={[16, 12]} align="middle">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.settings.services.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.settings.services.scene.filters.search_placeholder_name_or_title'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="auto" />
            <Col flex="none" className="filter-buttons-container">
              <Button
                type="primary"
                onClick={createResourceModal}
                disabled={tableLoading}
              >
                <PlusOutlined />{' '}
                {t('scenes.settings.services.scene.buttons.create_service')}
              </Button>
            </Col>
          </Row>
        </div>
      </Card>

      <Card style={{ marginTop: '12px' }}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table<MainType>
              dataSource={filteredDataSource}
              bordered
              loading={tableLoading}
              size="small"
              scroll={{ x: 1300, y: 640 }}
              pagination={false}
              rowKey={(record) => record.id}
              onChange={handleTableChange}
              columns={columns}
              components={{ body: { row: CustomRow } }}
            />
          </SortableContext>
        </DndContext>
      </Card>

      {/* Modals */}
      <NewServiceModal
        isOpen={isNewResourceModalOpen}
        handleModalCancel={handleCloseNewResourceModal}
        submit={createNewResource}
        countries={countries}
      />

      {selectedRow && (
        <DetailServiceModal
          id={selectedRow.id}
          isOpen={isDetailModalOpen}
          handleModalCancel={handleCloseShowDetailResourceModal}
          submit={editNewResource}
          countries={countries}
        />
      )}
    </Layout>
  );
}

export default Services;
